import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { apiClient } from "./client";

// Types — mirror of backend ``app.assessments.schemas``. Keep in lockstep.
//
// Note what is NOT here: no candidate-facing type carries a score, an
// outcome or a reviewer comment. Results are internal hiring material and
// a candidate never sees them, so the shapes they receive have nowhere to
// put one.

/** Which part of an exam a case belongs to. An exam contains both. */
export type TestPhase = "DRY_RUN" | "LIVE";

export type AttemptStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "SUBMITTED"
  | "EXPIRED"
  | "ABANDONED";

export interface TestSettings {
  dry_run_duration_seconds: number;
  live_default_duration_seconds: number;
  max_instances_per_case: number;
  /** Comma separated. Empty means nobody is told. */
  completion_notify_emails: string;
}

/**
 * One page of a list. Same shape the audit log uses — the admin console
 * has one pagination contract, not two.
 */
export interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
}

/**
 * What a PICKER asks for.
 *
 * A paged table and a "choose from all of them" checklist want opposite
 * things from the same endpoint. Rather than have pickers guess at page
 * numbers, they ask for one big page — and the server's own cap is what
 * bounds it.
 */
export const PICKER_PAGE_SIZE = 500;

export interface TestQuestion {
  id: string;
  prompt: string;
  kind: import("@/lib/questions").QuestionKind;
  options_json: string[];
  /** Must be answered before the candidate can reach the cases. */
  required: boolean;
  active: boolean;
  position: number;
  /** Which round this question may be drawn into. Absent on responses
   *  from a server predating rounds; treat that as "ROUND_1". */
  round?: import("@/lib/questions").Round;
  /** The section this sits under. Carried on the question rather than in
   *  a table of its own, so the seal freezes the heading too. */
  section_code: string | null;
  section_title: string | null;
  section_hint: string | null;
  help_text: string | null;
  placeholder: string | null;
  /** Kind-specific: SCALE and NUMBER_GRID carry their rows here. */
  config_json: import("@/lib/questions").QuestionConfig;
}

/** What the admin builder sends. Shared by create and edit so the two
 *  cannot drift into accepting different fields. */
export interface QuestionWriteBody {
  prompt?: string;
  kind?: string;
  options_json?: string[];
  required?: boolean;
  active?: boolean;
  position?: number;
  round?: import("@/lib/questions").Round;
  section_code?: string | null;
  section_title?: string | null;
  section_hint?: string | null;
  help_text?: string | null;
  placeholder?: string | null;
  config_json?: import("@/lib/questions").QuestionConfig;
}

export interface TestCase {
  id: string;
  title: string;
  modality: string | null;
  body_part: string | null;
  clinical_history: string | null;
  patient_sex: string | null;
  patient_age: string | null;
  status: string;
  instance_count: number;
  /**
   * How many of those instances the DIAGNOSTIC viewer can read.
   *
   * Equal to `instance_count` for anything ingested since the
   * constructed DICOM existed, and 0 for everything before it. There is
   * no conversion to run — the originals are discarded at ingest so
   * identifiers never reach disk — so migrating the library means
   * re-uploading it, and this is what says how much is left.
   */
  diagnostic_instance_count?: number;
  failed_instance_count: number;
  /** Exams currently listing this case. Can drop to zero. */
  exam_count: number;
  /** Candidates holding it on a sealed paper. Never drops. */
  assigned_count: number;
  created_at: string;
}

export interface TestDefinition {
  id: string;
  title: string;
  description: string | null;
  /** Which round this exam runs as. Absent from a server predating
   *  rounds; treat that as "ROUND_1". */
  kind?: import("@/lib/questions").Round;
  duration_seconds: number;
  /** NULL means this exam offers no practice run at all. */
  dry_run_duration_seconds: number | null;
  intro_prompt: string | null;
  selected_question_ids: string[];
  status: string;
  case_count: number;
  dry_run_case_count: number;
  created_at: string;
}

export interface DefinitionCase {
  id: string;
  test_case_id: string;
  phase: TestPhase;
  position: number;
  title: string | null;
  modality: string | null;
  body_part: string | null;
  clinical_history: string | null;
  /** VIDEO ROUND only: how long the candidate has to dictate this
   *  study. Null on a reporting exam, where the sitting shares one
   *  clock. The API has always sent it; nothing read it until the
   *  builder started setting it. */
  allotted_seconds: number | null;
}

export interface TestAssignment {
  id: string;
  test_definition_id: string;
  candidate_user_id: string;
  max_attempts: number;
  created_at: string;
  definition_title: string | null;
  has_dry_run: boolean;
  candidate_name: string | null;
  /** Digest of the paper THIS candidate was given. */
  sealed_fingerprint: string | null;
  sealed_case_count: number;
  /** Past this the paper is immutable — no re-seal, no withdraw. */
  has_begun: boolean;
  /** False once the exam has been edited since this candidate got it. */
  matches_current_exam: boolean;
  /** Latest attempt's state. null = never opened. */
  attempt_status: string | null;
  attempts_used: number;
  time_utilised_seconds: number | null;
  seconds_remaining: number | null;
  submitted_at: string | null;
}

export interface CandidateAssignment {
  assignment_id: string;
  title: string;
  description: string | null;
  /** Which round this is, and therefore which screen it opens. */
  kind?: import("@/lib/questions").Round;
  duration_seconds: number;
  /** Spoken questions in a video round, and the total of every item's
   *  own allowance -- questions and cases both. Zero for a reporting
   *  exam, which measures itself in cases against one overall clock. */
  question_count?: number;
  round_seconds?: number;
  has_dry_run: boolean;
  case_count: number;
  attempts_used: number;
  max_attempts: number;
  attempt_id: string | null;
  attempt_status: AttemptStatus | null;
  can_start: boolean;
}

export interface AttemptCase {
  assignment_case_id: string;
  position: number;
  title: string;
  modality: string | null;
  body_part: string | null;
  clinical_history: string | null;
  answered: boolean;
  /** Length of the candidate's own take. Null when `answered` is false. */
  recording_seconds: number | null;
}

export interface SnapshotQuestion {
  id: string;
  prompt: string;
  kind: string;
  options: string[];
  /**
   * Frozen with the rest of the snapshot. A question made mandatory after
   * this attempt was created must not retroactively block the candidate.
   */
  required?: boolean;
  /**
   * The section this question sat under, frozen with everything else.
   *
   * On the question rather than in a table of its own so the SEAL
   * captures the headings the candidate actually saw. Rename a section
   * tomorrow and a paper sealed today still shows what was asked.
   */
  section_code?: string | null;
  section_title?: string | null;
  section_hint?: string | null;
  /** Shown under the prompt. Often changes what the answer MEANS --
   *  "your sustainable number, not your best-ever day". */
  help_text?: string | null;
  placeholder?: string | null;
  /** Kind-specific. SCALE and NUMBER_GRID carry their rows here. */
  config?: import("@/lib/questions").QuestionConfig | null;
}

export interface Attempt {
  id: string;
  status: AttemptStatus;
  title: string;
  /**
   * Which round this sitting is.
   *
   * A reporting attempt and a screening attempt share a URL shape and
   * nothing else — no layout, no clock, no answer model. The reporting
   * page reads this and redirects rather than drawing an application
   * form for a round that has no application step.
   */
  kind?: import("@/lib/questions").Round;
  intro_prompt: string | null;
  questions: SnapshotQuestion[];
  intro_answers: Record<string, unknown>;
  intro_completed: boolean;
  started_at: string | null;
  expires_at: string | null;
  /** The SERVER's clock. The client anchors its countdown to this rather
   *  than to the local clock, which a candidate can change. */
  server_now: string;
  seconds_remaining: number;
  /** The whole allowance; seconds_remaining is 0 before the start. */
  duration_seconds: number;
  dry_run_duration_seconds: number | null;

  // The practice run. Its clock is entirely separate and counts for
  // nothing; `started` is what says the assessment itself has begun.
  has_dry_run: boolean;
  dry_run_started: boolean;
  dry_run_finished: boolean;
  dry_run_skipped: boolean;
  dry_run_seconds_remaining: number;
  started: boolean;

  dry_run_cases: AttemptCase[];
  cases: AttemptCase[];
}

export interface AttemptTick {
  status: AttemptStatus;
  server_now: string;
  expires_at: string | null;
  seconds_remaining: number;
}

export interface AttemptSummary {
  id: string;
  candidate_user_id: string;
  candidate_name: string | null;
  candidate_username: string | null;
  definition_title: string;
  /** Which exam, and which version of it this candidate sat. */
  test_definition_id: string | null;
  /** The ROUND. A panel sheet is keyed on this, not on the attempt. */
  test_assignment_id: string | null;
  sealed_fingerprint: string | null;
  status: AttemptStatus;
  attempt_number: number;
  started_at: string | null;
  submitted_at: string | null;
  expires_at: string | null;
  seconds_remaining: number | null;
  answered_count: number;
  case_count: number;
  /** How much of the allowance was actually spent. */
  time_utilised_seconds: number | null;
  dry_run_skipped: boolean;
  overall_score: number | null;
  overall_max_score: number | null;
  outcome: string | null;
  /**
   * Whether THE SIGNED-IN reviewer has marked their own screening of
   * this round complete. Per-viewer, because there is one sheet per
   * panel member: it is never a statement about the sitting itself.
   */
  my_screening_complete: boolean;
  my_screening_at: string | null;
}

export interface Review {
  id: string;
  test_answer_id: string | null;
  score: number | null;
  max_score: number | null;
  outcome: string | null;
  comments: string | null;
  reviewer_user_id: string | null;
  reviewer_name: string | null;
  updated_at: string;
}

export interface AnswerReview {
  answer_id: string | null;
  assignment_case_id: string;
  /** The library case behind the sealed row — what the viewer opens. */
  test_case_id: string;
  position: number;
  title: string;
  modality: string | null;
  clinical_history: string | null;
  /** The case context the candidate was shown. */
  body_part?: string | null;
  patient_sex?: string | null;
  patient_age?: string | null;
  recorded_at: string | null;
  late: boolean;
  has_audio: boolean;
  /** Whole seconds, read from the stored MP3 at upload. Null if ffprobe
   *  could not determine it -- a recording with an unknown length is
   *  still the candidate's answer and is never refused over it. */
  duration_seconds: number | null;
  /** Every reviewer's note on this case, not only the caller's. */
  reviews: Review[];
}

export interface AttemptReview {
  attempt: AttemptSummary;
  intro_prompt: string | null;
  questions: SnapshotQuestion[];
  intro_answers: Record<string, unknown>;
  integrity_events: { kind: string; at: string | null }[];
  answers: AnswerReview[];
  /** One overall verdict per reviewer. All reviewers see all of them. */
  overall_reviews: Review[];
}

export type ResultScope = "today" | "week" | "all";

// ---------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------

export const MY_ASSESSMENTS_KEY = ["tests", "me"] as const;
export const TEST_CASES_KEY = ["tests", "cases"] as const;
export const TEST_DEFINITIONS_KEY = ["tests", "definitions"] as const;
export const TEST_QUESTIONS_KEY = ["tests", "questions"] as const;
export const TEST_SETTINGS_KEY = ["tests", "settings"] as const;
export const TEST_ASSIGNMENTS_KEY = ["tests", "assignments"] as const;

export const attemptKey = (id: string) => ["tests", "attempt", id] as const;
export const attemptReviewKey = (id: string) =>
  ["tests", "attempt-review", id] as const;
export const attemptsKey = (
  scope: ResultScope,
  q: string,
  attemptStatus: string,
) => ["tests", "attempts", scope, q, attemptStatus] as const;

// ---------------------------------------------------------------------
// Candidate
// ---------------------------------------------------------------------

export async function fetchMyAssessments(): Promise<CandidateAssignment[]> {
  const { data } = await apiClient.get<CandidateAssignment[]>("/tests/me");
  return data;
}

export function useMyAssessments(
  enabled = true,
): UseQueryResult<CandidateAssignment[]> {
  return useQuery({
    queryKey: MY_ASSESSMENTS_KEY,
    queryFn: fetchMyAssessments,
    enabled,
    // ALWAYS REFETCH ON MOUNT. This list is the one screen a candidate
    // returns to after doing the very thing that changes it, and React
    // Query serves the cached copy on a client-side navigation --
    // `refetchOnWindowFocus` is off globally, so nothing else would
    // correct it. A candidate who had just submitted a round landed
    // back here and saw it still listed, offering "Resume", on a
    // sitting the SERVER had already closed. They could press it.
    refetchOnMount: "always",
  });
}

export async function startAttempt(assignmentId: string): Promise<Attempt> {
  const { data } = await apiClient.post<Attempt>("/tests/attempts", null, {
    params: { assignment_id: assignmentId },
  });
  return data;
}

export async function fetchAttempt(attemptId: string): Promise<Attempt> {
  const { data } = await apiClient.get<Attempt>(`/tests/attempts/${attemptId}`);
  return data;
}

export function useAttempt(attemptId: string | null): UseQueryResult<Attempt> {
  return useQuery({
    queryKey: attemptKey(attemptId ?? ""),
    queryFn: () => fetchAttempt(attemptId as string),
    enabled: !!attemptId,
    // The countdown is driven client-side from server_now; refetching on
    // an interval would fight it. Correction arrives via the heartbeat.
    staleTime: 30_000,
  });
}

export interface CaseImage {
  sop_instance_uid: string;
  series_instance_uid: string | null;
  series_number: number | null;
  series_description: string | null;
  instance_number: number | null;
  frame_number: number | null;
  width: number | null;
  height: number | null;
  /** "dicom" | "preset" | "auto" — whether the file specified the window. */
  window_source: string | null;
  /**
   * WHICH rendering this row is — "default", or a window the viewer can
   * switch to: "soft", "lung", "bone", "brain", or a name the file gave
   * itself.
   *
   * Several rows share one `sop_instance_uid` and differ only here. The
   * viewer groups on the uid and picks by this, so a stack is as long as
   * the number of slices and not the number of pictures.
   *
   * Optional on the wire for cases ingested before multi-window existed;
   * absent means "default".
   */
  window_label?: string;
  url: string;
  /**
   * The constructed de-identified DICOM for this INSTANCE, signed the
   * same way `url` is.
   *
   * Null for anything ingested before the diagnostic viewer, and for the
   * second and later windows of one instance -- there is one file per
   * instance, not per rendering. The viewer opens it when it is there
   * and falls back to `url` when it is not, which is what lets the
   * library migrate case by case.
   */
  dicom_url?: string | null;
  /**
   * Where this slice sits along the stack, and which plane it lies in.
   *
   * Together they are what decides whether a series can be REFORMATTED,
   * and they are on the wire rather than behind another endpoint so the
   * one place that groups a case into series is the one place that
   * judges them. Null for anything without geometry — an ultrasound
   * still, a plain film, anything ingested before they were stored —
   * which simply never offers MPR.
   */
  slice_position?: number | null;
  orientation_key?: string | null;
}

/**
 * A library case's images, read as an ADMIN rather than a candidate.
 *
 * Gated on UPLOAD_TEST_CASES, which a candidate does not hold — the
 * candidate route is scoped to their own attempt for exactly that reason.
 * This one exists so whoever assembles a case can look at what a
 * candidate will be shown before assigning it to anybody.
 */
export function useLibraryCaseImages(caseId: string | null) {
  return useQuery({
    queryKey: ["tests", "library-case-images", caseId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<CaseImage[]>(
        `/tests/cases/${caseId}/images`,
      );
      return data;
    },
    enabled: !!caseId,
  });
}

export const caseImagesKey = (attemptId: string, assignmentCaseId: string) =>
  ["tests", "case-images", attemptId, assignmentCaseId] as const;

export async function fetchCaseImages(
  attemptId: string,
  assignmentCaseId: string,
): Promise<CaseImage[]> {
  const { data } = await apiClient.get<CaseImage[]>(
    `/tests/attempts/${attemptId}/cases/${assignmentCaseId}/images`,
  );
  return data;
}

export function useCaseImages(
  attemptId: string,
  assignmentCaseId: string | null,
): UseQueryResult<CaseImage[]> {
  return useQuery({
    queryKey: caseImagesKey(attemptId, assignmentCaseId ?? ""),
    queryFn: () => fetchCaseImages(attemptId, assignmentCaseId as string),
    enabled: !!attemptId && !!assignmentCaseId,
    // Signed URLs last an hour; well beyond a sitting, and refetching
    // would swap every src and flash the viewer.
    staleTime: 30 * 60_000,
  });
}

/** Begin the practice run. Its clock is separate and counts for nothing. */
export async function startDryRun(attemptId: string): Promise<Attempt> {
  const { data } = await apiClient.post<Attempt>(
    `/tests/attempts/${attemptId}/dry-run/start`,
  );
  return data;
}

/** End or skip the practice run. `skipped` is recorded, not inferred. */
export async function finishDryRun(
  attemptId: string,
  skipped: boolean,
): Promise<Attempt> {
  const { data } = await apiClient.post<Attempt>(
    `/tests/attempts/${attemptId}/dry-run/finish`,
    null,
    { params: { skipped } },
  );
  return data;
}

/** "Start the test" — the deliberate act that starts the clock. */
export async function beginAssessment(attemptId: string): Promise<Attempt> {
  const { data } = await apiClient.post<Attempt>(
    `/tests/attempts/${attemptId}/begin`,
  );
  return data;
}

/** Leave for good. Irreversible; the assessment cannot be resumed. */
export async function exitAssessment(attemptId: string): Promise<AttemptTick> {
  const { data } = await apiClient.post<AttemptTick>(
    `/tests/attempts/${attemptId}/exit`,
  );
  return data;
}

export async function heartbeat(attemptId: string): Promise<AttemptTick> {
  const { data } = await apiClient.post<AttemptTick>(
    `/tests/attempts/${attemptId}/heartbeat`,
  );
  return data;
}

export async function saveIntro(
  attemptId: string,
  answers: Record<string, unknown>,
): Promise<AttemptTick> {
  const { data } = await apiClient.patch<AttemptTick>(
    `/tests/attempts/${attemptId}/intro`,
    { answers },
  );
  return data;
}

export async function completeIntro(attemptId: string): Promise<AttemptTick> {
  const { data } = await apiClient.post<AttemptTick>(
    `/tests/attempts/${attemptId}/intro/complete`,
  );
  return data;
}

export async function openCase(
  attemptId: string,
  assignmentCaseId: string,
): Promise<{ answer_id: string }> {
  const { data } = await apiClient.post<{ answer_id: string }>(
    `/tests/attempts/${attemptId}/cases/${assignmentCaseId}/open`,
  );
  return data;
}

export async function recordIntegrityEvent(
  attemptId: string,
  kind: string,
): Promise<void> {
  // Fire-and-forget. An integrity signal that fails to send must never
  // interrupt the candidate — it is context for a reviewer, not a gate.
  try {
    await apiClient.post(`/tests/attempts/${attemptId}/events`, {
      kind,
      at: new Date().toISOString(),
    });
  } catch {
    /* ignored on purpose */
  }
}

export interface RecordingResult {
  answer_id: string;
  job_id: string;
  /** Arrived after the deadline. Kept and flagged, never discarded. */
  late: boolean;
  status: string;
}

export async function uploadRecording(
  attemptId: string,
  assignmentCaseId: string,
  audio: Blob,
): Promise<RecordingResult> {
  const form = new FormData();
  // A filename is required for the multipart part to be treated as a
  // file by Starlette; the extension is cosmetic since the server
  // transcodes whatever arrives.
  form.append("audio", audio, "recording.webm");
  const { data } = await apiClient.post<RecordingResult>(
    `/tests/attempts/${attemptId}/cases/${assignmentCaseId}/recording`,
    form,
    // Longer than the default: a several-minute report is a real upload
    // over clinic wifi, and timing it out would discard the take.
    { timeout: 120_000 },
  );
  return data;
}

/**
 * The candidate's own take, for playing back.
 *
 * A separate route from the reviewer's `/answers/{id}/audio`, which
 * stays behind MANAGE_TESTS. This one is addressed by attempt + sealed
 * case -- there is no answer id to substitute -- and the server refuses
 * unless the attempt is still open.
 */
export async function fetchOwnRecording(
  attemptId: string,
  assignmentCaseId: string,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/tests/attempts/${attemptId}/cases/${assignmentCaseId}/recording`,
    { responseType: "blob" },
  );
  return data;
}

/** Throw the take away, leaving the case unanswered. Audited server-side. */
export async function deleteOwnRecording(
  attemptId: string,
  assignmentCaseId: string,
): Promise<void> {
  await apiClient.delete(
    `/tests/attempts/${attemptId}/cases/${assignmentCaseId}/recording`,
  );
}

export async function submitAttempt(attemptId: string): Promise<AttemptTick> {
  const { data } = await apiClient.post<AttemptTick>(
    `/tests/attempts/${attemptId}/submit`,
  );
  return data;
}

// ---------------------------------------------------------------------
// Authoring
// ---------------------------------------------------------------------

export async function fetchTestSettings(): Promise<TestSettings> {
  const { data } = await apiClient.get<TestSettings>("/tests/settings");
  return data;
}

export function useTestSettings(): UseQueryResult<TestSettings> {
  return useQuery({ queryKey: TEST_SETTINGS_KEY, queryFn: fetchTestSettings });
}

export function usePatchTestSettings(): UseMutationResult<
  TestSettings,
  unknown,
  Partial<TestSettings>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<TestSettings>) => {
      const { data } = await apiClient.patch<TestSettings>(
        "/tests/settings",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_SETTINGS_KEY });
    },
  });
}

export async function fetchQuestions(
  includeInactive = false,
): Promise<TestQuestion[]> {
  const { data } = await apiClient.get<TestQuestion[]>("/tests/questions", {
    params: { include_inactive: includeInactive },
  });
  return data;
}

export function useQuestions(
  includeInactive = false,
  page = 1,
  pageSize = 50,
  round?: import("@/lib/questions").Round,
) {
  return useQuery({
    queryKey: [
      ...TEST_QUESTIONS_KEY,
      { includeInactive, page, pageSize, round },
    ] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<Page<TestQuestion>>(
        "/tests/questions",
        {
          params: {
            ...(includeInactive ? { include_inactive: true } : {}),
            ...(round ? { round } : {}),
            page,
            page_size: pageSize,
          },
        },
      );
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

/**
 * Every active question in one round, for the exam builder's picker.
 *
 * Scoped by round rather than listing the whole pool: a video round's
 * picker showing the reporting exam's application questions would offer
 * the admin a choice that cannot work, and the mistake would only
 * surface when a candidate met a typed field on a camera screen.
 */
export function useAllQuestions(round?: import("@/lib/questions").Round) {
  return useQuery({
    queryKey: [...TEST_QUESTIONS_KEY, "all", round ?? "any"] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<Page<TestQuestion>>(
        "/tests/questions",
        {
          params: {
            page: 1,
            page_size: PICKER_PAGE_SIZE,
            ...(round ? { round } : {}),
          },
        },
      );
      return data;
    },
    select: (p: Page<TestQuestion>) => p.items,
  });
}

export async function fetchLastSelection(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>(
    "/tests/questions/last-selection",
  );
  return data;
}

/**
 * Edit or retire a question.
 *
 * There is no delete, deliberately: an attempt freezes the questions it
 * actually asked (``questions_snapshot_json``), but the pool row is what
 * an exam's ``selected_question_ids`` points at. Retiring sets
 * ``active=false`` — it drops out of the picker and leaves history intact.
 */
export function usePatchQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      questionId: string;
      body: QuestionWriteBody;
    }) => {
      const { data } = await apiClient.patch<TestQuestion>(
        `/tests/questions/${args.questionId}`,
        args.body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_QUESTIONS_KEY });
    },
  });
}

export function useCreateQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuestionWriteBody & { prompt: string; kind: string }) => {
      const { data } = await apiClient.post<TestQuestion>(
        "/tests/questions",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_QUESTIONS_KEY });
    },
  });
}

export async function fetchCases(
  q?: string,
  includeArchived = false,
  page = 1,
  pageSize = 50,
): Promise<Page<TestCase>> {
  const { data } = await apiClient.get<Page<TestCase>>("/tests/cases", {
    params: {
      ...(q ? { q } : {}),
      ...(includeArchived ? { include_archived: true } : {}),
      page,
      page_size: pageSize,
    },
  });
  return data;
}

export function useCases(
  q?: string,
  includeArchived = false,
  page = 1,
  pageSize = 50,
) {
  return useQuery({
    queryKey: [
      ...TEST_CASES_KEY,
      q ?? "",
      { includeArchived, page, pageSize },
    ] as const,
    queryFn: () => fetchCases(q, includeArchived, page, pageSize),
    // Keep the previous page on screen while the next loads, rather than
    // blanking the table on every click.
    placeholderData: (prev) => prev,
  });
}

/** Every case, for a picker. See PICKER_PAGE_SIZE. */
export function useAllCases() {
  return useQuery({
    queryKey: [...TEST_CASES_KEY, "all"] as const,
    queryFn: () => fetchCases(undefined, false, 1, PICKER_PAGE_SIZE),
    select: (p: Page<TestCase>) => p.items,
  });
}

/** Edit a library case's title, modality, body part or brief. */
export function usePatchCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      caseId: string;
      body: Record<string, unknown>;
    }) => {
      const { data } = await apiClient.patch<TestCase>(
        `/tests/cases/${args.caseId}`,
        args.body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

/**
 * Archive a case. Never a hard delete.
 *
 * A case a candidate was assessed on has to survive as long as the review
 * does; the database refuses to drop it, and the image destructor refuses
 * too. Archiving takes it out of the picker and leaves everything intact.
 */
export function useArchiveCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      await apiClient.delete(`/tests/cases/${caseId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

/**
 * Remove every image from a case, so a different study can be uploaded.
 *
 * The way to REPLACE images: clear, then upload. Refused once a
 * candidate has been assigned the case — their recording describes these
 * pictures.
 */
/**
 * Make a case available to exams.
 *
 * The deliberate "this is finished" step. Until it happens the case is a
 * draft: previewable and editable, but no exam may include it.
 */
export function usePublishCase() {
  const qc = useQueryClient();
  return useMutation({
    /**
     * `acceptIncomplete` is the admin having been TOLD what was lost and
     * saying yes anyway. It is never sent on the first attempt: the
     * server refuses with the count, the caller shows it, and only a
     * second, deliberate press carries the flag.
     */
    mutationFn: async ({
      caseId,
      acceptIncomplete = false,
    }: {
      caseId: string;
      acceptIncomplete?: boolean;
    }) => {
      const { data } = await apiClient.post<TestCase>(
        `/tests/cases/${caseId}/publish`,
        {},
        acceptIncomplete ? { params: { accept_incomplete: true } } : undefined,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

/** Back to draft. Refused while an exam still lists the case. */
export function useUnpublishCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const { data } = await apiClient.post<TestCase>(
        `/tests/cases/${caseId}/unpublish`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

export function useClearCaseImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const { data } = await apiClient.delete<TestCase>(
        `/tests/cases/${caseId}/images`,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

export function useRestoreCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const { data } = await apiClient.post<TestCase>(
        `/tests/cases/${caseId}/restore`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      title: string;
      modality?: string | null;
      body_part?: string | null;
      clinical_history?: string | null;
      deident_confirmed: boolean;
    }) => {
      const { data } = await apiClient.post<TestCase>("/tests/cases", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
    },
  });
}

export interface InstanceUploadResult {
  stored: number;
  skipped: boolean;
  duplicate: boolean;
  /** Skipped because it was never an image (report, PDF). Not a failure. */
  not_an_image: boolean;
  /** The CASE is full. Every remaining file will be refused identically. */
  at_capacity: boolean;
  reason: string | null;
  modality: string | null;
  burned_in_annotation: boolean;
  instance_count: number;
  failed_instance_count: number;
}

export async function uploadCaseInstance(
  caseId: string,
  file: File,
): Promise<InstanceUploadResult> {
  const form = new FormData();
  // The filename is sent because multipart requires one, but the server
  // never reads or stores it — export filenames routinely contain a
  // patient surname.
  form.append("file", file, "instance.dcm");
  const { data } = await apiClient.post<InstanceUploadResult>(
    `/tests/cases/${caseId}/instances`,
    form,
    { timeout: 120_000 },
  );
  return data;
}

export async function finalizeCase(caseId: string): Promise<TestCase> {
  const { data } = await apiClient.post<TestCase>(
    `/tests/cases/${caseId}/finalize`,
  );
  return data;
}

export interface PurgeSummary {
  attempts: number;
  answers: number;
  recordings: number;
  assignments: number;
  account_deactivated: boolean;
}

export async function purgeCandidate(
  candidateUserId: string,
  confirmUsername: string,
): Promise<PurgeSummary> {
  const { data } = await apiClient.post<PurgeSummary>(
    `/tests/candidates/${candidateUserId}/purge`,
    null,
    { params: { confirm_username: confirmUsername } },
  );
  return data;
}

export interface DefinitionQuery {
  includeArchived?: boolean;
  /** Title contains. */
  q?: string;
  /** Only exams this candidate has been assigned. */
  candidateUserId?: string;
  page?: number;
  pageSize?: number;
}

export async function fetchDefinitions(
  query: DefinitionQuery = {},
): Promise<Page<TestDefinition>> {
  const { data } = await apiClient.get<Page<TestDefinition>>(
    "/tests/definitions",
    {
      params: {
        ...(query.includeArchived ? { include_archived: true } : {}),
        ...(query.q ? { q: query.q } : {}),
        ...(query.candidateUserId
          ? { candidate_user_id: query.candidateUserId }
          : {}),
        page: query.page ?? 1,
        page_size: query.pageSize ?? 50,
      },
    },
  );
  return data;
}

export function useDefinitions(query: DefinitionQuery = {}) {
  return useQuery({
    queryKey: [...TEST_DEFINITIONS_KEY, query] as const,
    queryFn: () => fetchDefinitions(query),
    placeholderData: (prev) => prev,
  });
}

/** One case on an exam, as the create and edit routes accept it. */
export interface DefinitionCaseInput {
  test_case_id: string;
  phase: TestPhase;
  /** Video round only: this case's own clock. */
  allotted_seconds?: number;
}

export function useCreateDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      title: string;
      kind?: string;
      description?: string | null;
      duration_seconds: number;
      dry_run_duration_seconds?: number | null;
      intro_prompt?: string | null;
      selected_question_ids: string[];
      /** Saved in the same transaction as the exam: a refused case
       *  refuses the whole create, so nothing is left behind. */
      cases?: DefinitionCaseInput[];
    }) => {
      const { data } = await apiClient.post<TestDefinition>(
        "/tests/definitions",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
    },
  });
}

export async function fetchDefinitionCases(
  definitionId: string,
): Promise<DefinitionCase[]> {
  const { data } = await apiClient.get<DefinitionCase[]>(
    `/tests/definitions/${definitionId}/cases`,
  );
  return data;
}

/** An exam's own case list — what the builder loads when editing. */
export function useDefinitionCases(definitionId: string | null) {
  return useQuery({
    queryKey: [...TEST_DEFINITIONS_KEY, definitionId, "cases"] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<DefinitionCase[]>(
        `/tests/definitions/${definitionId}/cases`,
      );
      return data;
    },
    enabled: !!definitionId,
  });
}

/**
 * Edit an exam.
 *
 * No longer refused once the exam has been sat: candidates hold their
 * own sealed copy, so an edit reaches only those assigned afterwards.
 */
export function usePatchDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      definitionId: string;
      body: Record<string, unknown>;
    }) => {
      const { data } = await apiClient.patch<TestDefinition>(
        `/tests/definitions/${args.definitionId}`,
        args.body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
    },
  });
}

export async function fetchAssignments(): Promise<TestAssignment[]> {
  const { data } = await apiClient.get<TestAssignment[]>("/tests/assignments");
  return data;
}

export function useAssignments() {
  return useQuery({
    queryKey: TEST_ASSIGNMENTS_KEY,
    queryFn: fetchAssignments,
    // This list reports what candidates are DOING right now -- attending,
    // time remaining, completed -- and it changes without the admin
    // touching anything. The app disables focus refetching globally
    // (a considered choice for clinical screens, where a background
    // refresh under someone's cursor is worse than slightly old data),
    // but that left an admin console open on a hiring round showing
    // "Opened, not started" for a candidate who had already finished and
    // gone home. Both are re-enabled here, for this query only.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      test_definition_id: string;
      candidate_user_id: string;
      max_attempts: number;
    }) => {
      const { data } = await apiClient.post<TestAssignment>(
        "/tests/assignments",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_ASSIGNMENTS_KEY });
    },
  });
}

/**
 * Withdraw an exam from a candidate.
 *
 * The backend refuses once the candidate has started, so this can only
 * ever undo a mistake — it cannot erase a sitting.
 */
/**
 * Copy an exam so the next round can start from the last one.
 *
 * The counterpart to the edit lock: a sat exam is frozen for good
 * reason, so the way to add this month's new cases is to copy it, not to
 * rebuild it.
 */
export function useDuplicateDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (definitionId: string) => {
      const { data } = await apiClient.post<TestDefinition>(
        `/tests/definitions/${definitionId}/duplicate`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
    },
  });
}

/** Retire an exam. Archived, never deleted — results outlive it. */
/** Bring a retired exam back. It returns as a draft, not to its old state. */
export function useRestoreDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (definitionId: string) => {
      const { data } = await apiClient.post<TestDefinition>(
        `/tests/definitions/${definitionId}/restore`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
    },
  });
}

export interface BulkRefusal {
  id: string;
  title: string;
  reason: string;
}

export interface BulkActionResult {
  affected: string[];
  refused: BulkRefusal[];
  missing: string[];
}

/** The server's cap, and the list endpoint's. Batches are chunked to it. */
export const BULK_DELETE_LIMIT = 500;

/**
 * Retire or destroy exams in bulk.
 *
 * TWO ROUTES, not one taking a flag, and the client keeps that shape:
 * they differ in whether anything can be undone, so an endpoint that
 * accepts either is one wrong value away from destroying what somebody
 * meant to file away.
 *
 * Retiring only stops an exam being offered and refuses one being SAT
 * right now. Deleting is the only call here that really destroys.
 *
 * Everything else is archive-and-restore. The server refuses any exam
 * that was ever assigned, because the foreign keys cascade from the
 * definition down through the sitting to the answers and reviews, and
 * `audio_key` lives on a row that would go with them — so an unguarded
 * delete strands the recordings on disk with nothing left to name them.
 *
 * Chunked rather than sent whole: a library of thousands exceeds the
 * server's batch cap, and one request that either destroys 1400 rows or
 * times out is a worse shape than a sequence with somewhere to stop.
 * The per-id outcomes are merged so the caller reports once.
 */
export type BulkExamAction = "retire" | "delete";

async function bulkExamAction(
  action: BulkExamAction,
  ids: string[],
  onProgress?: (done: number, total: number) => void,
): Promise<BulkActionResult> {
  const merged: BulkActionResult = { affected: [], refused: [], missing: [] };
  for (let i = 0; i < ids.length; i += BULK_DELETE_LIMIT) {
    const chunk = ids.slice(i, i + BULK_DELETE_LIMIT);
    const { data } = await apiClient.post<BulkActionResult>(
      `/tests/definitions/${action}`,
      { ids: chunk },
    );
    merged.affected.push(...data.affected);
    merged.refused.push(...data.refused);
    merged.missing.push(...data.missing);
    onProgress?.(Math.min(i + chunk.length, ids.length), ids.length);
  }
  return merged;
}

export type BulkCaseAction = "archive" | "restore";

/**
 * Archive or restore a selection of cases.
 *
 * **THERE IS NO BULK DELETE, and that is the library's oldest rule.**
 * `test_assignment_cases.test_case_id` is ON DELETE RESTRICT — the
 * database refuses to destroy a case a candidate was assessed on,
 * because the review record would lose the thing it was reviewing. So
 * the pair here is archive/restore, not retire/delete.
 *
 * Restore exists because a bulk archive that could only be undone one
 * row at a time is a one-way door for anybody who selects 1900 and
 * means 19.
 */
async function bulkCaseAction(
  action: BulkCaseAction,
  ids: string[],
  onProgress?: (done: number, total: number) => void,
): Promise<BulkActionResult> {
  const merged: BulkActionResult = { affected: [], refused: [], missing: [] };
  for (let i = 0; i < ids.length; i += BULK_DELETE_LIMIT) {
    const chunk = ids.slice(i, i + BULK_DELETE_LIMIT);
    const { data } = await apiClient.post<BulkActionResult>(
      `/tests/cases/${action}`,
      { ids: chunk },
    );
    merged.affected.push(...data.affected);
    merged.refused.push(...data.refused);
    merged.missing.push(...data.missing);
    onProgress?.(Math.min(i + chunk.length, ids.length), ids.length);
  }
  return merged;
}

export function useBulkCaseAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      action: BulkCaseAction;
      ids: string[];
      onProgress?: (done: number, total: number) => void;
    }) => bulkCaseAction(args.action, args.ids, args.onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_CASES_KEY });
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
    },
  });
}

export function useBulkExamAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      action: BulkExamAction;
      ids: string[];
      onProgress?: (done: number, total: number) => void;
    }) => bulkExamAction(args.action, args.ids, args.onProgress),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
      void qc.invalidateQueries({ queryKey: TEST_ASSIGNMENTS_KEY });
    },
  });
}

export function useArchiveDefinition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (definitionId: string) => {
      await apiClient.delete(`/tests/definitions/${definitionId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_DEFINITIONS_KEY });
      void qc.invalidateQueries({ queryKey: TEST_ASSIGNMENTS_KEY });
    },
  });
}

/**
 * Hand a candidate the exam as it stands now.
 *
 * Only meaningful while they have not begun — after that the backend
 * refuses, because the paper is theirs.
 */
/**
 * Grant or withdraw a re-sit.
 *
 * A re-sit reuses the SAME seal — the candidate sits the paper they were
 * given, not whatever the exam says today — which is what makes this
 * safe to do in the middle of a hiring round.
 */
export function usePatchAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { assignmentId: string; maxAttempts: number }) => {
      const { data } = await apiClient.patch<TestAssignment>(
        `/tests/assignments/${args.assignmentId}`,
        { max_attempts: args.maxAttempts },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_ASSIGNMENTS_KEY });
    },
  });
}

export function useResealAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { data } = await apiClient.post<TestAssignment>(
        `/tests/assignments/${assignmentId}/reseal`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_ASSIGNMENTS_KEY });
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      await apiClient.delete(`/tests/assignments/${assignmentId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TEST_ASSIGNMENTS_KEY });
    },
  });
}

// ---------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------

export async function fetchAttempts(args: {
  scope: ResultScope;
  q?: string;
  /** One of the TestAttemptStatus values, e.g. "SUBMITTED". */
  attemptStatus?: string;
  /** The reviewer's worklist filter: "awaiting" | "scored". */
  reviewState?: string;
  page?: number;
  pageSize?: number;
}): Promise<Page<AttemptSummary>> {
  const { data } = await apiClient.get<Page<AttemptSummary>>(
    "/tests/attempts",
    {
      params: {
        scope: args.scope,
        ...(args.q ? { q: args.q } : {}),
        // The backend query param is `attempt_status`. The old `kind`
        // this used to send was never read there, so it filtered
        // nothing -- removed rather than left as a decoy.
        ...(args.attemptStatus ? { attempt_status: args.attemptStatus } : {}),
        ...(args.reviewState ? { review_state: args.reviewState } : {}),
        page: args.page ?? 1,
        page_size: args.pageSize ?? 50,
      },
    },
  );
  return data;
}

export function useAttempts(args: {
  scope: ResultScope;
  q?: string;
  attemptStatus?: string;
  reviewState?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: [
      ...attemptsKey(args.scope, args.q ?? "", args.attemptStatus ?? ""),
      { reviewState: args.reviewState ?? "", page: args.page ?? 1, pageSize: args.pageSize ?? 50 },
    ] as const,
    queryFn: () => fetchAttempts(args),
    placeholderData: (prev) => prev,
    // In-progress attempts show a live remaining time, so the list needs
    // to move on its own -- and coming back to the tab should not show
    // the state as it was when the admin walked away.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export async function fetchAnswerAudio(answerId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/tests/answers/${answerId}/audio`,
    { responseType: "blob" },
  );
  return data;
}

export async function fetchAttemptReview(
  attemptId: string,
): Promise<AttemptReview> {
  const { data } = await apiClient.get<AttemptReview>(
    `/tests/attempts/${attemptId}/review`,
  );
  return data;
}

export function useAttemptReview(attemptId: string | null) {
  return useQuery({
    queryKey: attemptReviewKey(attemptId ?? ""),
    queryFn: () => fetchAttemptReview(attemptId as string),
    enabled: !!attemptId,
  });
}

export function useSaveReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      attemptId: string;
      answerId: string | null;
      body: {
        score: number | null;
        max_score: number | null;
        outcome: string | null;
        comments: string | null;
      };
    }) => {
      const url =
        args.answerId === null
          ? `/tests/attempts/${args.attemptId}/review`
          : `/tests/attempts/${args.attemptId}/reviews/${args.answerId}`;
      const { data } = await apiClient.put<Review>(url, args.body);
      return data;
    },
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: attemptReviewKey(args.attemptId) });
      void qc.invalidateQueries({ queryKey: ["tests", "attempts"] });
    },
  });
}

// ---------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------

export interface DashboardStats {
  /** Counted over ASSIGNMENTS — see `queued`. */
  total: number;
  /**
   * Assigned and not yet attended: no attempt on the assignment has left
   * NOT_STARTED. Counted over assignments, NOT attempts, because an
   * attempt row is not created until the candidate opens the exam — so
   * an assignment nobody has opened is invisible to every attempt query.
   *
   * This is why the Assessments view's "Queued" filter (which asks for
   * `attempt_status=NOT_STARTED`) is NARROWER than this number, and why
   * the Queued tile does not link to it.
   */
  queued: number;
  in_progress: number;
  awaiting_valuation: number;
  scored: number;
  expired: number;
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ["tests", "dashboard"] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<DashboardStats>("/tests/dashboard");
      return data;
    },
    // The dashboard reports what candidates are doing right now, so it
    // refreshes on its own and when the tab regains focus -- the same
    // reasoning as the sittings list.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}


// ---------------------------------------------------------------------
// The video screening round
// ---------------------------------------------------------------------

export interface ScreeningState {
  total: number;
  answered: number;
  index: number;
  /**
   * A case item carries `kind: "CASE"` and the sealed case to open. The
   * camera stays on the candidate throughout — the dictation is the part
   * where knowing who is speaking matters most — so the viewer appears
   * BESIDE the self-view rather than instead of it.
   */
  /** The sealed question, exactly as it was asked. Null once finished. */
  question: SnapshotQuestion | null;
  /**
   * Computed by the SERVER and sent as a number, not a deadline.
   *
   * The candidate's machine may carry any clock at all, so the only one
   * both ends can agree on is the one the answer came from. The screen
   * counts down from this on `performance.now()` and re-anchors every
   * time the server answers again.
   */
  seconds_remaining: number | null;
  finished: boolean;
  status: AttemptStatus;
  closed_reason: string | null;
  disconnections: number;
  max_disconnections: number;
}

export interface ScreeningRecording {
  duration_seconds: number | null;
  /** Signed and short-lived; see the route for why it is a URL. */
  url: string | null;
  chapters: ScreeningChapter[];
}

export interface ScreeningChapter {
  index: number;
  question_id: string;
  allotted_seconds: number;
  started_at: string;
  ended_at: string | null;
  paused_seconds: number;
  start_offset: number;
  end_offset: number | null;
  ended_by: "next" | "timer" | string | null;
}

/**
 * The current question and what is left of its clock.
 *
 * DOUBLES AS THE HEARTBEAT: `last_seen_at` moving is what lets the next
 * call work out how long the candidate was gone, and therefore how much
 * of their allowance to give back. Polling it is not a spare request.
 */
export async function fetchScreeningState(
  attemptId: string,
): Promise<ScreeningState> {
  const { data } = await apiClient.get<ScreeningState>(
    `/tests/attempts/${attemptId}/screening/state`,
  );
  return data;
}

/** Serve the first question and start its clock. */
export async function beginScreening(
  attemptId: string,
): Promise<ScreeningState> {
  const { data } = await apiClient.post<ScreeningState>(
    `/tests/attempts/${attemptId}/screening/begin`,
  );
  return data;
}

/**
 * Close the current question and open the next.
 *
 * `offsetSeconds` is where the recorder is inside the file — the client
 * is the only party that knows it. `endedBy` is advisory: the server
 * re-checks the deadline either way.
 */
export async function advanceScreening(
  attemptId: string,
  offsetSeconds: number,
  endedBy: "next" | "timer",
): Promise<ScreeningState> {
  const { data } = await apiClient.post<ScreeningState>(
    `/tests/attempts/${attemptId}/screening/advance`,
    { offset_seconds: Math.max(0, Math.round(offsetSeconds)), ended_by: endedBy },
  );
  return data;
}

/**
 * Hand over one fragment of the recording.
 *
 * Posted WHILE recording rather than at the end: one attempt with no
 * restart means a blob delivered at Submit loses the whole sitting to a
 * crash at minute fourteen.
 */
export async function uploadVideoChunk(
  attemptId: string,
  segment: number,
  sequence: number,
  blob: Blob,
): Promise<void> {
  const form = new FormData();
  form.append("segment", String(segment));
  form.append("sequence", String(sequence));
  form.append("chunk", blob, `s${segment}-c${sequence}.webm`);
  await apiClient.post(`/tests/attempts/${attemptId}/video/chunks`, form);
}

/** Join every fragment into the file the assessor will watch. */
export async function finaliseVideo(
  attemptId: string,
): Promise<ScreeningRecording> {
  const { data } = await apiClient.post<ScreeningRecording>(
    `/tests/attempts/${attemptId}/video/finalise`,
  );
  return data;
}

/** The assembled sitting, for whoever is assessing it. */
export function useScreeningRecording(attemptId: string | null) {
  return useQuery({
    queryKey: ["tests", "screening", attemptId] as const,
    enabled: !!attemptId,
    queryFn: async () => {
      const { data } = await apiClient.get<ScreeningRecording>(
        `/tests/attempts/${attemptId}/screening`,
      );
      return data;
    },
  });
}
